/**
 * WarrantInfoModals — Round P142·1 (Feb 2026)
 *
 * Two shared click-to-open modals used by the Stock Warrant card in
 * the modal + SEO page:
 *
 *   • <ExchangeInfoDialog code="XNYS" />  — full name, location,
 *     brief history of the exchange the warrant trades on.
 *   • <ExercisePriceDialog price={11.50} />  — plain-English
 *     explanation of what "exercise price" means for a warrant and
 *     how the standard $11.50 SPAC strike works.
 *
 * Both use shadcn Dialog which already:
 *   - renders at z-[200] (above the StockModal at z-[150]),
 *   - closes on outside click / Esc,
 *   - includes a top-right X button.
 */
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';

// Static reference data — MIC codes for the exchanges DiviDrip
// commonly sees on warrants. Sourced from ISO 10383 + each exchange's
// own "About" page. Kept short (2-3 sentences of history) so the
// modal stays scannable.
const EXCHANGE_INFO = {
  XNYS: {
    fullName: 'New York Stock Exchange',
    location: 'New York City, NY, USA',
    history:
      'Founded 1792 under the Buttonwood Agreement, the NYSE is the world\u2019s largest stock exchange by ' +
      'listed-company market cap. Now operated by Intercontinental Exchange (ICE) after its 2013 acquisition. ' +
      'Most large-cap dividend payers list here (JNJ, KO, XOM, VZ). Ticker suffix `.WS` indicates a warrant on ' +
      'NYSE-listed securities.',
  },
  XNAS: {
    fullName: 'Nasdaq Stock Market',
    location: 'New York City, NY, USA',
    history:
      'Launched 1971 as the world\u2019s first fully electronic exchange \u2014 no trading floor. Dominant venue ' +
      'for tech (AAPL, MSFT, NVDA, GOOGL) and most SPACs. Nasdaq warrants get a `W` suffix appended to the ' +
      'underlying (e.g. IONQ \u2192 IONQW), not the `.WS` convention NYSE uses.',
  },
  XASE: {
    fullName: 'NYSE American',
    location: 'New York City, NY, USA',
    history:
      'Formerly the American Stock Exchange (AMEX). Acquired by NYSE Euronext in 2008 and renamed NYSE American ' +
      'in 2017. Focused on small-cap and micro-cap growth companies plus ETFs; hosts many BDCs and non-dividend ' +
      'growth stocks that don\u2019t yet qualify for the main NYSE Board.',
  },
  ARCX: {
    fullName: 'NYSE Arca',
    location: 'Chicago, IL, USA (formerly Pacific Exchange, San Francisco)',
    history:
      'Fully electronic ECN acquired by NYSE in 2006 (rebranded from ArcaEx). The primary US listing venue ' +
      'for ETFs \u2014 nearly all major ETF sponsors (SPDR, iShares, Vanguard, Invesco) list their funds here. ' +
      'Warrants on ARCX-listed vehicles are rare but exist for a few closed-end funds.',
  },
  BATS: {
    fullName: 'Cboe BZX Exchange',
    location: 'Kansas City, MO, USA (parent Cboe based in Chicago)',
    history:
      'Founded 2005 as Better Alternative Trading System, acquired by Cboe in 2017. Second-largest US equity ' +
      'venue by volume behind NYSE + Nasdaq. Handful of ETFs list directly on BZX; most equity/warrant tickers ' +
      'here are cross-listed from other venues.',
  },
  IEXG: {
    fullName: 'Investors Exchange (IEX)',
    location: 'New York City, NY, USA',
    history:
      'Founded 2012 by Brad Katsuyama (of Michael Lewis\u2019 "Flash Boys" fame). Uses a 350-microsecond speed ' +
      'bump to blunt high-frequency-trading advantages. Small footprint on warrant listings but a growing ' +
      'venue for market-structure-aware investors.',
  },
};

export function ExchangeInfoDialog({ code, open, onOpenChange }) {
  const info = EXCHANGE_INFO[(code || '').toUpperCase()];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="warrant-exchange-info-dialog">
        <DialogHeader>
          <DialogTitle className="font-mono">{code} &middot; Exchange Info</DialogTitle>
          <DialogDescription>
            The Market Identifier Code (MIC) tells you which venue the warrant trades on.
          </DialogDescription>
        </DialogHeader>
        {info ? (
          <div className="space-y-3 text-sm">
            <div>
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Full Name</div>
              <div className="font-semibold">{info.fullName}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Location</div>
              <div>{info.location}</div>
            </div>
            <div className="rounded-md bg-muted/40 p-3 text-xs leading-relaxed">
              {info.history}
            </div>
          </div>
        ) : (
          <div className="text-sm text-muted-foreground">
            No reference entry for the MIC code <span className="font-mono">{code}</span>. This is a lesser-known
            trading venue \u2014 check the ISO 10383 registry for the full name and location.
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function ExercisePriceDialog({ price, open, onOpenChange }) {
  const priceLabel = price != null ? `$${Number(price).toFixed(2)}` : null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="warrant-exercise-price-dialog">
        <DialogHeader>
          <DialogTitle>Exercise Price {priceLabel ? `\u00b7 ${priceLabel}` : ''}</DialogTitle>
          <DialogDescription>
            The strike price you pay to convert one warrant into one share of the underlying common stock.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          {priceLabel && (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Strike</div>
              <div className="font-mono text-lg font-bold">{priceLabel} <span className="text-xs font-normal text-muted-foreground">per share</span></div>
            </div>
          )}
          <div className="rounded-md bg-muted/40 p-3 text-xs leading-relaxed space-y-2">
            <p>
              <span className="font-semibold">How it works:</span> To exercise the warrant, you pay the exercise
              price {priceLabel || '(the strike)'} and receive one share of common stock in return. Your total cost
              per share is <span className="font-mono">exercise price + what you paid for the warrant</span>.
            </p>
            <p>
              <span className="font-semibold">Break-even:</span> The underlying must trade ABOVE that combined cost
              for exercising to be profitable. Below break-even and the warrant is out-of-the-money \u2014 exercising
              would lose money vs just buying shares in the open market.
            </p>
            <p>
              <span className="font-semibold">The $11.50 standard:</span> Almost every US SPAC IPO uses an $11.50
              exercise price (11.50 = 10.00 IPO + 15% premium). If the warrant is from a SPAC and the exercise
              price is blank, $11.50 is a safe assumption \u2014 verify in the linked SEC filing.
            </p>
            <p>
              <span className="font-semibold">Cashless exercise:</span> Some warrants let you settle in shares
              instead of cash \u2014 you surrender warrants worth the strike amount and receive the net shares.
              Check the warrant\u2019s prospectus for the exact terms.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
